import React from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import radioStyles from 'styles/radio';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import visuallyHidden from 'styles/visuallyHidden';
import RawHtml from '../common/RawHtml';
import rootStyles from './styles/root';

const styles = (theme) => {
  const { spacing, typography } = theme;
  return {
    input: radioStyles(theme),
    label: {
      ...typography.p,
      ...typography.p2,
      display: 'block',
      margin: [0, 60, 0, 12],
      padding: [0, spacing.unit, 0, spacing.unit],
    },
    legend: {
      ...typography.p,
      ...typography.p2,
      paddingBottom: spacing.xs,
      width: '100%',
    },
    radio: {
      display: 'flex',
      flexDirection: 'row',
      float: 'left',
      maxWidth: '50%',
    },
    root: rootStyles({ spacing }),
    visuallyHidden,
  };
};

const BooleanSelector = ({
  classes,
  error,
  falseLabel,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  label,
  loading,
  name,
  onChange,
  placeholder,
  readOnly,
  required,
  tooltip,
  trueLabel,
  validate,
  value,
}) => {
  const optionalLabel = !required
    ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>`
    : '';
  const labelString = `${label}${optionalLabel}`;
  const buttons = [
    {
      changeValue: true,
      radioLabel: trueLabel,
    },
    {
      changeValue: false,
      radioLabel: falseLabel,
    },
  ];
  return (
    <fieldset
      id={`${fieldDomId}`}
      className={classnames('form-inline', 'form-group', { error, loading }, classes.root)}
    >
      <legend className={classes.legend}>
        <RawHtml type="span">{labelString}</RawHtml>
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={2} />
        <Help help={help} id={`help-${name}`} />
        {required && <span className={classnames('hidden', classes.visuallyHidden)}>Required</span>}
      </legend>
      <Spinner loading={loading} />
      {buttons.map((button) => (
        <div
          key={`${name}-${button.radioLabel}`}
          className={classnames('radio form-group', classes.radio)}
        >
          <input
            type="radio"
            aria-describedby={
              classnames({
                [`help-${name}`]: help,
                [`error-${name}`]: error,
              }) || undefined
            }
            name={fieldDomId}
            id={`${fieldDomId}-${button.changeValue}`}
            placeholder={placeholder}
            checked={value === button.changeValue}
            onChange={() => {
              if (!readOnly) {
                onChange(name, button.changeValue);
                validate();
              }
            }}
            readOnly={readOnly}
            className={classes.input}
            value={button.changeValue}
          />
          <RawHtml
            type="label"
            htmlFor={`${fieldDomId}-${button.changeValue}`}
            className={classes.label}
          >
            {button.radioLabel}
          </RawHtml>
        </div>
      ))}
      <ErrorMsg name={name} />
    </fieldset>
  );
};

BooleanSelector.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  falseLabel: PropTypes.string.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  trueLabel: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
};

const BooleanSelectorStyled = withStyles(styles)(BooleanSelector);

const mapStateToProps = ({ fields, resources }, { name }) => ({
  falseLabel: fields[name].falseLabel,
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix ?? '',
  trueLabel: fields[name].trueLabel,
});

export default withFieldProps(mapStateToProps)(BooleanSelectorStyled);
